import { useNavigate } from "react-router-dom";
import { BookOpen, Users, ShieldCheck, ArrowRight, Mail, HelpCircle, PhoneCall, Globe } from "lucide-react";

// Logo import
import logoImg from "../../assets/logo.jpeg";

// Direct imports from MasterCssClass.js
import {
  masterAuthRoot,
  masterAuthGlowLeft,
  masterAuthGlowRight,
  masterHomeBgUrl,
  masterHomeRootContainer,
  masterHomeInlineStyle,
  masterHomeHeader,
  masterHomeLogoBox,
  masterHomeLogoImg,
  masterHomeNavTitle,
  masterHomeSignInBtn,
  masterHomeGetStartedBtn,
  masterHomeHeroSection,
  masterHomeHeroOverlay,
  masterHomeHeroContent,
  masterHomeBadge,
  masterHomeHeroTitle,
  masterHomeDivider,
  masterHomeHeroDesc,
  masterHomeExploreBtn,
  masterHomeSectionPadding,
  masterHomeSectionWrapper,
  masterHomeSectionHeader,
  masterHomeSectionTitle,
  masterHomeSectionSubTitle,
  masterHomeGridCards,
  masterHomeCardItem,
  masterHomeCardIconBox1,
  masterHomeCardIconBox2,
  masterHomeCardIconBox3,
  masterHomeCardTitle,
  masterHomeCardDesc,
  masterHomeFooter,
  masterHomeFooterInner,
  masterHomeFooterTitle,
  masterHomeFooterDesc,
  masterHomeFooterBtn,
  masterHomeFooterBottom,
  masterHomeFooterTextSm,
  masterHomeFooterTextWhite,
  masterHomeFooterCopyright,
} from "../../GlobalComponents/MasterCssClass";

const HomeAbout = () => {
  const navigate = useNavigate();

  return (
    <div className={masterAuthRoot}>
      <div className={masterAuthGlowLeft}></div>
      <div className={masterAuthGlowRight}></div>

      {/* Main Container */}
      <div className={masterHomeRootContainer} style={masterHomeInlineStyle}>
        
        {/* NAVBAR */}
        <header className={masterHomeHeader}>
          <div className="flex items-center gap-2.5">
            <div className={masterHomeLogoBox}>
              <img src={logoImg} alt="Medhashala Logo" className={masterHomeLogoImg} />
            </div>
            <span className={masterHomeNavTitle}>MEDHASHALA</span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/login")} className={masterHomeSignInBtn}>
              Sign In
            </button>
            <button onClick={() => navigate("/login")} className={masterHomeGetStartedBtn}>
              Get Started
            </button>
          </div>
        </header>

        {/* HERO SECTION */}
        <section className={masterHomeHeroSection} style={{ backgroundImage: `url(${masterHomeBgUrl})` }}>
          <div className={masterHomeHeroOverlay}></div>

          <div className={masterHomeHeroContent}>
            <div className={masterHomeBadge}>
              <span>Your Path to Smarter Learning</span>
            </div>
            
            <h1 className={masterHomeHeroTitle}>
              Empowering Aspirants & Institutions with Intelligent Education Management
            </h1>
            
            <div className={masterHomeDivider}></div>
            
            <p className={masterHomeHeroDesc}>
              Medhashala is a comprehensive ed-tech platform built to streamline multi-tenant mock examinations, institutional referrals, automated evaluations, and performance tracking.
            </p>

            <div className="pt-2">
              <button onClick={() => navigate("/login")} className={masterHomeExploreBtn}>
                <span>Explore Portal</span> <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </section>

        {/* ABOUT US / FEATURES SECTION */}
        <section className={masterHomeSectionPadding}>
          <div className={masterHomeSectionWrapper}>
            
            <div className={masterHomeSectionHeader}>
              <h2 className={masterHomeSectionTitle}>About Medhashala</h2>
              <div className="mx-auto h-[2px] w-10 bg-[#F4B429]"></div>
              <p className={masterHomeSectionSubTitle}>
                Designed for precision, speed, and seamless institutional collaboration.
              </p>
            </div>

            {/* Feature Cards Grid */}
            <div className={masterHomeGridCards}>
              
              {/* Card 1 */}
              <div className={masterHomeCardItem}>
                <div className={masterHomeCardIconBox1}>
                  <BookOpen size={20} />
                </div>
                <h3 className={masterHomeCardTitle}>Multi-Tenant Examinations</h3>
                <p className={masterHomeCardDesc}>
                  Targeted mock exams configured specifically for government and institutional standards under unique tenant keys.
                </p>
              </div>

              {/* Card 2 */}
              <div className={masterHomeCardItem}>
                <div className={masterHomeCardIconBox2}>
                  <Users size={20} />
                </div>
                <h3 className={masterHomeCardTitle}>Institutional Referrals</h3>
                <p className={masterHomeCardDesc}>
                  Empower schools and coaching organizations to onboard students securely using validated active referral codes.
                </p>
              </div>

              {/* Card 3 */}
              <div className={masterHomeCardItem}>
                <div className={masterHomeCardIconBox3}>
                  <ShieldCheck size={20} />
                </div>
                <h3 className={masterHomeCardTitle}>Secure Authentication & OTP</h3>
                <p className={masterHomeCardDesc}>
                  Robust role-based access control with email verification OTPs, password recovery, and admin approvals.
                </p>
              </div>

            </div>

          </div>
        </section>

        {/* ENHANCED CLEAR FOOTER */}
        <footer className={masterHomeFooter}>
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-left pb-6 border-b border-white/10">
            
            {/* Col 1: Brand Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img src={logoImg} alt="Logo" className="h-full w-full object-cover" />
                </div>
                <span className="font-black text-sm text-white tracking-[0.05em]">MEDHASHALA</span>
              </div>
              <p className="text-[11px] text-slate-300 font-medium leading-relaxed tracking-[0.05em]">
                Your trusted digital ecosystem for multi-tenant mock test series, institutional onboarding, and comprehensive performance analytics.
              </p>
            </div>

            {/* Col 2: Quick Links */}
            <div className="space-y-2">
              <h4 className="font-black text-xs text-[#F4B429] uppercase tracking-[0.05em]">Quick Navigation</h4>
              <ul className="space-y-1.5 text-xs font-semibold text-slate-300">
                <li>
                  <button onClick={() => navigate("/login")} className="hover:text-white transition-colors cursor-pointer tracking-[0.05em]">
                    Sign In to Portal
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate("/login")} className="hover:text-white transition-colors cursor-pointer tracking-[0.05em]">
                    Student & Organization Registration
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate("/login")} className="hover:text-white transition-colors cursor-pointer tracking-[0.05em]">
                    Password Recovery & Support
                  </button>
                </li>
              </ul>
            </div>

            {/* Col 3: Clear Support Details */}
            <div className="space-y-2">
              <h4 className="font-black text-xs text-[#F4B429] uppercase tracking-[0.05em]">Help & Technical Support</h4>
              <div className="space-y-1.5 text-xs text-slate-300 font-semibold">
                <p className="flex items-center gap-2 tracking-[0.05em]">
                  <Mail size={13} className="text-[#F4B429] shrink-0" />
                  <span className="text-white font-bold select-all">support.medhashala@gmail.com</span>
                </p>
                <p className="flex items-center gap-2 tracking-[0.05em]">
                  <HelpCircle size={13} className="text-[#F4B429] shrink-0" />
                  <span>Available 24/7 for Portal Assistance</span>
                </p>
              </div>
            </div>

          </div>

          {/* Bottom Copyright */}
          <div className="pt-4 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between text-[10px] text-slate-400 font-medium tracking-[0.05em]">
            <p>&copy; 2026 Medhashala. All rights reserved.</p>
            <p className="text-slate-500 mt-1 sm:mt-0">System-generated secure educational platform.</p>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default HomeAbout;